import React from 'react';
import {
    Navigation,
    Page,
    TaskMultiSelect,
    Storage,
    TaskTabPanel,
    TaskImageClick,
    TaskMultiChoice,
    Image
} from '@bittnerdevelopmentgroup/interactive';
import { Row, Col } from 'react-bootstrap';

import p1 from "./assets/Int_PrinEco5_31b-1_Custom.jpg"
import p2 from "./assets/PrinEco4_LongRun_p2.jpg"
import p3 from "./assets/PrinEco4_LongRun_p3.jpg"
import p4 from "./assets/PrinEco4_LongRun_p4.jpg"
import p5 from "./assets/PrinEco4_LongRun_p5.jpg"
import p5a from "./assets/PrinEco4_LongRun_p5a.jpg"

import "./styles.css"

function LongRun() {
    return <div className="longRun">
        <div className="sr-only" aria-hidden="true">
            {[p1, p2, p3, p4, p5, p5a].map(src => <img alt="" src={src} />)}
        </div>
        <Navigation interactiveId="chapter-31b">
            <Page>
                <p>
                    In the short run, expansionary monetary policy can increase output and lower unemployment. In the
                    long run, however, all prices adjust. Let’s see why using the example of a skate shop you own.
                </p>
                <p align='center'>
                    <Image
                        src={p1}
                        alt='A photo of a person using a screwdriver to adjust the metal truck on the underside of a skateboard.'
                        photoCredit={"Shutterstock / guruXOX"}
                        width='70%'
                    />
                </p>
                <p>
                    After the Fed lowers interest rates, your bank offers you a loan. You use it to open a second skate shop
                    in an old storefront that needs some renovations. You’ll have to stock the shop with skateboards,
                    wheels, and gear, and hire new employees, so you know your costs will rise. But based on your
                    projections, the $10,000 in monthly revenue you expect from the new location will be enough to cover
                    those expenses and still leave a profit.
                </p>
                <TaskMultiChoice
                    state={Storage()}
                    prompt={<><b>Given what you’ve learned about the short-run effects of monetary policy, which of those expenses do you think will increase in the first few months after receiving the loan (the short run)?</b></>}
                    choices={["Mortgage payments", "Labor costs", "Inventory costs", "Monthly internet bill"]}
                    correct={[2]}
                    feedback={[
                        "Remember, your mortgage payment is set by your loan contract and won’t change right away.",
                        "Wages tend to be sticky and aren’t likely to shift in the short run.",
                        "As demand rises, you need to order more skateboards and gear, which increases your shipping and restocking costs in the short run.",
                        "This is typically a fixed monthly expense that doesn’t change right away when business picks up.",
                    ]}
                />
            </Page>
            <Page>
                <p align='center'>
                    <Image
                        src={p2}
                        alt='Empty storefront with paper over the windows.'
                        photoCredit={"Architect´s Eye/Alamy Stock Photo"}
                        width='70%'
                    />
                </p>
                <TaskMultiSelect
                    state={Storage()}
                    prompt={<><b>Which of those costs might change in the year that it takes to build the storefront and hold the grand opening (i.e., in the long run)? </b><i>Select all that apply.</i></>}
                    data={[
                        {
                            label: 'Mortgage payments ',
                            correct: false,
                        },
                        {
                            label: 'Labor costs',
                            correct: true,
                        },
                        {
                            label: 'Inventory costs',
                            correct: true,
                        },
                        {
                            label: 'None of these answers is correct.',
                            correct: false,
                        },
                    ]}
                    correctFeedback="In the long run, prices that were slow to adjust in the short run, like wages and the prices of goods from suppliers, can rise as demand increases. That means both labor costs and the cost of restocking inventory may increase over time."
                    incorrectFeedback="Consider how different types of costs behave over time. Which costs are fixed and unlikely to change, and which are flexible and may increase as demand rises?"
                />
            </Page>
            <Page>
                <Row>
                    <Col xs={6} md={6} style={{ margin: 'auto' }}>
                        <p align='center'>
                            <Image
                                src={p3}
                                alt='A photo of a person using a calculator while holding receipts over an open notebook.'
                                width='90%'
                                photoCredit={"Christian Horz/Alamy Stock Photo"}
                            />
                        </p>
                    </Col>
                    <Col xs={12} md={6}>
                        <p>
                            In the case of your business, labor costs have gone up to $4,000 per month and inventory costs have gone up to $3,000 per month. With your $4,000 mortgage, this means that the $10,000 per month you have allocated no longer covers your costs.
                        </p>
                        <p>
                            This leads to one of two possible scenarios.
                        </p>
                    </Col>
                </Row>
                <TaskTabPanel
                    state={Storage()}
                    label="Possible scenarios."
                    contents={[
                        {
                            title: "Scenario 1",
                            contents: "The monetary expansion leads to new demand for your skateboards and apparel! You have more money to allocate to the new location and everything is fine.",
                        },
                        {
                            title: "Scenario 2",
                            contents: "Demand for your skateboards and apparel doesn’t increase. When the price of resources rises, you find that you can’t afford them. You have to reduce your inventory, lay off your employees, or even close your location."
                        },
                    ]}
                />
            </Page>
            <Page>
                <p>
                    This same type of situation is happening all over the country. Let’s examine the effects of that using the AD–AS (aggregate demand–aggregate supply) model.
                </p>
                <p>
                    In the short run, when the money supply increases, aggregate demand increases. <b>Where does that place the new, short-run equilibrium?</b>
                </p>
                <p>
                    <i>Select the correct point on the following graph.</i>
                </p>
                <TaskImageClick
                    state={Storage()}
                    hotspots={[
                        {
                            correct: false,
                            label: 'Point where A D 1 intersects with S R A S 1',
                            feedback: 'You’re looking for the intersection of the new aggregate demand curve and short-run aggregate supply.',
                            style: {
                                top: "160px",
                                left: "321px",
                                width: "30px",
                                height: "30px",
                            },
                        },
                        {
                            correct: true,
                            label: 'Point where A D 2 intersects with S R A S 1',
                            feedback: 'In the short run, as demand increases, the price level increases slightly and real GDP also increases.',
                            style: {
                                top: "122px",
                                left: "358px",
                                width: "30px",
                                height: "30px",
                            },
                        },
                        {
                            correct: false,
                            label: 'Point on A D 2, at higher price level than intersection with S R A S 1',
                            feedback: 'You’re looking for the intersection of the new aggregate demand curve and short-run aggregate supply.',
                            style: {
                                top: "86px",
                                left: "321px",
                                width: "30px",
                                height: "30px",
                            },
                        },
                    ]}
                >
                    <Image
                        src={p4}
                        alt="Graph illustrates the aggregate demand and aggregate supply. Real GDP (Y, in trillions of dollars) is on the x axis and Price level (P) in on the y axis. Two negatively sloping lines are marked AD1 and AD2, a positively sloping diagonal line intersects and is marked SRAS1. SRAS crosses with AD1 at Y1 and P1. SRAS crosses with AD2 at Y2 and P2."
                        width='65%'
                        style={{ display: "flex", justifyContent: "center", margin: "auto" }}
                    />
                </TaskImageClick>
            </Page>
            <Page>
                <p>
                    Then, in the long run, resource prices adjust.
                </p>
                <p align="center">
                    <img
                        src={p4}
                        alt="Graph illustrates the aggregate demand and aggregate supply. Real GDP (Y, in trillions of dollars) is on the x axis and Price level (P) in on the y axis. Two negatively sloping lines are marked AD1 and AD2, a positively sloping diagonal line intersects and is marked SRAS1. SRAS crosses with AD1 at Y1 and P1. SRAS crosses with AD2 at Y2 and P2."
                        width="70%"
                    />
                </p>
                <TaskMultiChoice
                    state={Storage()}
                    prompt={<b>What does that do to the aggregate supply curve?</b>}
                    choices={["shifts to the right", "shifts to the left"]}
                    correct={[1]}
                    feedback={[
                        "If resource prices are increasing, what will that do to supply?",
                        <div tabIndex={0}>
                            <p>
                                As time goes on, resource prices will increase. That increases the cost of production, decreasing profits and causing producers to produce fewer products. Aggregate supply decreases at every price level, causing the curve to shift to the left.
                            </p>
                            <p align="center">
                                <img
                                    src={p5a}
                                    alt="A line graph. There is Real GDP (Y) on the x axis and Price level (P) on the y axis. Two negative sloping parallel lines are marked AD1 and AD2 and an arrow points from AD1 to AD2. Two positively sloping parallel lines intersect and are marked as SR AS1 and SRAS2. Another vertical line marked L R A S runs through all of the other lines on the graph. Where LRAS, AD1, and SRAS1 cross is at Y1 in real GDP and at P1, labeled A. Where LRAS, AD2, and SRAS2 cross is at Y1 and P3, labeled C. Last, where AD2 and SRAS1 cross is Y2 and at P2, labeled b."
                                    width="80%"
                                />
                            </p>
                            <p>
                                That shift to the left causes a new equilibrium (C). As you can tell, that shifts real GDP back to the original value, but now the price level has increased.
                            </p>
                        </div>,
                    ]}
                />
            </Page>
            <Page>
                <p>
                    As you can tell, inflation is a very predictable effect of monetary policy. So, why does the Fed do it?
                </p>
                <p>
                    Well, economists sometimes need to make a judgment call, and this is one of those situations. Is it better to prioritize the short run, easing the suffering of job loss, food insecurity, and poverty in the wake of a financial downturn? Or is it better to prioritize long-run stability, taking measures to reduce future inflation as much as possible?
                </p>
                <p>
                    There are macroeconomists on both sides of the debate. By learning to analyze the effects of monetary policy, you will be able to make that judgment for yourself.
                </p>
            </Page>
        </Navigation>
    </div>
}

export default LongRun